import { getCurrentPlatformTheme } from '@/helpers/platform';
import { DefaultPrivacyLevel } from '@datadog/browser-core';
import { type RumErrorEvent, type RumEvent } from '@datadog/browser-rum';
import { captureError, initBrowserRum } from '@smg-real-estate/browser-rum';

export enum CustomAction {
  SearchLocation = 'Search Location',
  MapClick = 'Map Click',
}

export enum DDActionName {
  SearchInput = 'Search Input',
}

const isAbortError = (error: RumErrorEvent['error']) => {
  const msg = error.message ?? '';
  return (
    error.name === 'AbortError' ||
    msg.includes('AbortError') ||
    msg.includes('Fetch is aborted') ||
    msg.includes('abortTile')
  );
};

const isConsoleError = (error: RumErrorEvent['error']) => {
  return error?.source === 'console';
};

const is3rdPartyScript = (stack: string) => {
  return stack && !stack.includes('/market-atlas-assets/');
};

const isBrowserExtension = (stack: string) => {
  return stack.includes('chrome-extension://') || stack.includes('moz-extension://');
};

export const initDatadogRUM = (): void => {
  if (!import.meta.env.VUE_APP_PLATFORM) {
    return;
  }

  initBrowserRum({
    applicationId: import.meta.env.VUE_DATADOG_RUM_APPLICATION_ID,
    clientToken: import.meta.env.VUE_DATADOG_RUM_CLIENT_TOKEN,
    service: getCurrentPlatformTheme() + '-market-atlas-vue',
    env: import.meta.env.VUE_STAGE,
    version: import.meta.env.VUE_COMMIT_SHA,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 10,
    defaultPrivacyLevel: DefaultPrivacyLevel.ALLOW,
    beforeSend: (event: RumEvent) => {
      if (event.type === 'error') {
        const stack = event.error?.stack || '';

        if (
          isAbortError(event.error) ||
          isConsoleError(event.error) ||
          is3rdPartyScript(stack) ||
          isBrowserExtension(stack)
        ) {
          return false;
        }
      }

      return true;
    },

    customAttributes: {
      platform: import.meta.env.VUE_APP_PLATFORM,
    },
    trackingConsentEnabled: true,
    enabled: ['test', 'prod'].includes(import.meta.env.VUE_STAGE),
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    processErrorWithNoDuplication(error.message, () => {
      error.dd_fingerprint = 'global-unhandled-rejection';
      captureError(error);
    });
  });
};

const errorCache = new Set<string>();
export const ERROR_THROTTLE_TIME = 5000; // Do not report the same error more than once every 5 seconds

export const processErrorWithNoDuplication = (
  errorMessage: string,
  errorReportingCallback: () => void,
  timeout = ERROR_THROTTLE_TIME,
): void => {
  if (!errorCache.has(errorMessage)) {
    errorCache.add(errorMessage);
    errorReportingCallback();

    setTimeout(() => errorCache.delete(errorMessage), timeout);
  } else {
    console.warn('Duplicate error ignored:', errorMessage);
  }
};
